import { Autocomplete, Box, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import debounce from 'lodash/debounce';
import { gql, useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import SearchResultItem from '../common/search/SearchResultItem';
import { SearchUsersQuery } from '~/graphql/__generated__/graphql';

interface Option {
  title: string;
  id: number;
  icon: JSX.Element;
  subtitle1: string | null | undefined;
  subtitle2: string;
}

const generateUserTitle = (user: NonNullable<SearchUsersQuery["searchUser"][number]>) => {
  const { firstName, lastName, providerFirstName, providerLastName } = user;

  const providerName = (
    // Don't include the provider name if the user isn't a provider
    (!providerFirstName && !providerLastName)
    // or if it matches the user's name.'
    || (firstName === providerFirstName && lastName === providerLastName)
  ) ? '' : `(${providerFirstName} ${providerLastName}) `

  return `${firstName} ${lastName} ${providerName}(${user.email})`.trim();
};

const UserSearchBox: React.FC = () => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [searchTriggered, setSearchTriggered] = useState(false); // Track if a search was triggered

  const [searchUsers, { data, loading }] = useLazyQuery<SearchUsersQuery>(SEARCH_USERS);

  const notFoundItems = useMemo(() => [{ title: '', id: -1, icon: <NoAccountsIcon />, subtitle1: '', subtitle2: '' }], []);

  const debouncedSearch = useMemo(
    () =>
      debounce((inputValue: string) => {
        const searchTerm = inputValue;
        if (inputValue.trim() !== '') {
          setSearchTriggered(true);
          searchUsers({
            variables: {
              searchTerm,
              limit: 10,
              offset: 0,
              hideSystemAccounts: true,
            },
          });
        }
      }, 300),
    [searchUsers]
  );

  const options = useMemo(() => {
    if (!data?.searchUser || !searchTriggered) {
      return [];
    }
    if (data.searchUser.length === 0) {
      return notFoundItems;
    }

    const formatRoles = (roles?: string[] | null) => {
      if(!roles) {
        return roles;
      }
      return roles.join(", ");
    }

    const userOptions: Option[] = data.searchUser.filter(u => u !== null).map((user) => ({
      title: generateUserTitle(user),
      id: user.id,
      icon: user.active ? <PersonIcon /> : <PersonOffIcon />,
      subtitle1: `${user.active ? "" : "INACTIVE "}${formatRoles(user.roles)} ${user.providerType}`,
      subtitle2: "",
    }));
    return userOptions;
  }, [data, notFoundItems, searchTriggered]);

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, value: string, reason: string) => {
      if (reason === 'reset') {
        setInputValue('');
        setSearchTriggered(false);
      } else {
        setInputValue(value);
        if (value.length >= 3) {
          debouncedSearch(value);
        } else {
          setSearchTriggered(false);
        }
      }
    },
    [debouncedSearch, setSearchTriggered]
  );

  const handleOptionSelect = (_event: React.SyntheticEvent, value: NonNullable<string | Option>) => {
    setInputValue('');
    if (value && typeof value === 'object' && value.id) {
      navigate(`/users/manage/${value.id}`);
    }
  };

  return (
    <Box sx={{ minWidth: 500 }}>
      <Autocomplete
        freeSolo
        id="user-search-autocomplete"
        disableClearable
        options={options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        fullWidth
        size="small"
        onChange={handleOptionSelect}
        filterOptions={(x) => x}
        loading={loading}
        loadingText='Searching...'
        renderInput={(params) => (
          <TextField
            variant="outlined"
            sx={{
              backgroundColor: 'white',
              borderRadius: '4px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderRadius: 'inherit',
                },
              },
              '& .MuiInputBase-input': {
                color: 'gray',
              },
            }}
            {...params}
            placeholder='Search users by name or email'
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    {loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  </InputAdornment>
                ),
              }
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...restProps } = props;

          if (option.id === -1 && searchTriggered && !loading) {
            return (
              <Box
                component="li"
                key={`${key}`}
                {...restProps}
                onClick={() => {}}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  {option.icon}
                </Box>
                <Typography variant="body2" color="textSecondary">
                  No matches found
                </Typography>
              </Box>
            );
          }
          return (
            <SearchResultItem
              key={key}
              itemProps={restProps}
              inputValue={inputValue}
              {...option}
            />
          );
        }}
      />
    </Box>
  );
};

export default UserSearchBox;

const SEARCH_USERS = gql`
  query SearchUsers($searchTerm: String, $limit: Int, $offset: Int, $hideSystemAccounts: Boolean)
  @permission(requires: manageUsers)
  {
    searchUser(searchTerm: $searchTerm, limit: $limit, offset: $offset, hideSystemAccounts: $hideSystemAccounts) {
      id
      email
      firstName
      lastName
      roles
      active
      providerType
      providerFirstName
      providerLastName
    }
  }
`;
